/**
 * Forum mentions: posts from the forum on Byggahus.se that mention a product or collection.
 *
 * - Dates from the last week read "2 tim sedan" or "igår". The page itself is cached, so this runs in the browser.
 * - "Visa fler" loads the next posts from the address the forum gives in data-next-url (forum.mentions' next_url),
 *   and fills a copy of the markup in <template data-forum-template> (snippets/forum-mention.liquid) for each.
 *   Each page names the address of the one after.
 * - On the homepage, each "Visa fler" shows two more rows of cards, three cards on phones, and when the cards on
 *   the page run short, fetches the section's next page from Shopify (sections/forum-latest.liquid).
 */
(function () {
  'use strict';

  if (window.ForumMentions) {
    window.ForumMentions.refresh(document);
    return;
  }

  var MONTHS = ['jan', 'feb', 'mar', 'apr', 'maj', 'jun', 'jul', 'aug', 'sep', 'okt', 'nov', 'dec'];

  function formatNumber(number) {
    return Number(number || 0).toLocaleString('sv-SE');
  }

  function absoluteDate(date) {
    var text = date.getDate() + ' ' + MONTHS[date.getMonth()];

    if (date.getFullYear() !== new Date().getFullYear()) {
      text += ' ' + date.getFullYear();
    }

    return text;
  }

  function relativeDate(date) {
    var seconds = Math.max(0, (Date.now() - date.getTime()) / 1000);

    if (seconds < 60) return 'nyss';
    if (seconds < 3600) return Math.floor(seconds / 60) + ' min sedan';
    if (seconds < 86400) return Math.floor(seconds / 3600) + ' tim sedan';

    var days = Math.floor(seconds / 86400);

    if (days === 1) return 'igår';
    if (days < 7) return days + ' dagar sedan';

    return absoluteDate(date);
  }

  function updateDates(root) {
    var times = root.querySelectorAll('time[data-forum-time]');

    for (var i = 0; i < times.length; i++) {
      var date = new Date(times[i].getAttribute('datetime'));

      if (!isNaN(date.getTime())) {
        times[i].textContent = relativeDate(date);
        times[i].title = absoluteDate(date) + ' ' + date.toTimeString().slice(0, 5);
      }
    }
  }

  // The post's own address with the UTM tags before its #anchor, as in snippets/forum-mention.liquid
  function forumLink(url, campaign) {
    var parts = String(url || '').split('#');
    var base = parts[0];

    base += (base.indexOf('?') === -1 ? '?' : '&') + 'utm_source=byggahus-shop&utm_medium=forum-mentions&utm_campaign=' + encodeURIComponent(campaign);

    return parts.length > 1 ? base + '#' + parts.slice(1).join('#') : base;
  }

  function setText(element, text) {
    if (element) element.textContent = text;
  }

  function removeElement(element) {
    if (element && element.parentNode) element.parentNode.removeChild(element);
  }

  // On a phone the filter's pills are one row that scrolls sideways when they don't fit (theme.css): a chosen pill
  // out of sight, or cut at an edge, slides into view, without the page itself scrolling
  function revealChoice(pill) {
    var row = pill && pill.closest('.forum-latest__filter');

    if (!row || row.scrollWidth <= row.clientWidth) return;

    // Where the pill sits along the row's whole scrolling width
    var padding = parseFloat(getComputedStyle(row).paddingLeft) || 0;
    var left = pill.getBoundingClientRect().left - row.getBoundingClientRect().left + row.scrollLeft;
    var right = left + pill.getBoundingClientRect().width;

    if (left - padding < row.scrollLeft) {
      row.scrollTo({ left: left - padding, behavior: 'smooth' });
    } else if (right + padding > row.scrollLeft + row.clientWidth) {
      row.scrollTo({ left: right + padding - row.clientWidth, behavior: 'smooth' });
    }
  }

  // Such a row fades out at an edge with more pills beyond it (theme.css), however the pills fall at the window's edge
  function watchEdges(row) {
    if (row.hasAttribute('data-forum-edges')) return;
    row.setAttribute('data-forum-edges', '');

    function update() {
      var end = row.scrollWidth - row.clientWidth;

      row.classList.toggle('is-scrollable-left', row.scrollLeft > 1);
      row.classList.toggle('is-scrollable-right', row.scrollLeft < end - 1);
    }

    row.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', update);
    window.addEventListener('load', update);
    update();
  }

  function fillQuote(element, excerpt, matched) {
    element.textContent = '';

    var index = matched ? excerpt.indexOf(matched) : -1;

    if (index === -1) {
      element.textContent = excerpt;
      return;
    }

    var mark = document.createElement('mark');
    mark.textContent = matched;

    element.appendChild(document.createTextNode(excerpt.slice(0, index)));
    element.appendChild(mark);
    element.appendChild(document.createTextNode(excerpt.slice(index + matched.length)));
  }

  // The yellow mark. The forum's matched_text is only the first word of a phrase that matched with words between, as
  // "Fresh" in "Fresh Flow FDX" for the keyword "Fresh FDX", and empty when the match is in the thread's title or in
  // a link. The mark is widened here to the rest of the keyword (data-forum-keywords): its other words within three
  // words of it, before or after, as the forum's phrases allow. When the post's text holds no keyword, the thread's
  // title gets the mark instead.
  var KEYWORD_SLOP = 3;

  // Letters and digits, lower-cased, with where each word starts and ends: "Grön-Fri" is two words, as in a keyword
  function textWords(text) {
    var words = [];
    var pattern = /[\p{L}\p{N}]+/gu;
    var found;

    while ((found = pattern.exec(text))) {
      words.push({ word: found[0].toLowerCase(), start: found.index, end: found.index + found[0].length });
    }

    return words;
  }

  // A keyword's word in a text, from its start as the forum stems it ("Crackern" is "Cracker"). One or two letters,
  // or a number, must match whole, so "D-1200" doesn't take every word on d.
  function sameWord(word, keywordWord) {
    if (keywordWord.length <= 2 || /^\d+$/.test(keywordWord)) return word === keywordWord;

    return word.indexOf(keywordWord) === 0 && word.length <= keywordWord.length + 4;
  }

  // The characters to mark in a text: from matched text when the text holds it, widened to its keyword's other words,
  // else the first keyword whose words all stand within reach of each other. [start, end], or null.
  function markSpan(text, matched, keywords) {
    var words = textWords(text);
    var phrases = [];

    for (var p = 0; p < keywords.length; p++) {
      var phrase = textWords(String(keywords[p])).map(function (entry) {
        return entry.word;
      });

      if (phrase.length) phrases.push(phrase);
    }

    // The words from-to widened by the phrase's other words, each the nearest within reach, or null without them all
    function widen(phrase, from, to) {
      var reach = KEYWORD_SLOP + phrase.length - 1;
      var first = from;
      var last = to;

      for (var k = 0; k < phrase.length; k++) {
        var hit = -1;
        var nearest = Infinity;

        for (var j = Math.max(0, from - reach); j <= Math.min(words.length - 1, to + reach); j++) {
          if (!sameWord(words[j].word, phrase[k])) continue;

          var distance = j < from ? from - j : j > to ? j - to : 0;

          if (distance < nearest) {
            nearest = distance;
            hit = j;
          }
        }

        if (hit === -1) return null;

        first = Math.min(first, hit);
        last = Math.max(last, hit);
      }

      return [first, last];
    }

    var at = matched ? text.toLowerCase().indexOf(matched.toLowerCase()) : -1;

    if (at !== -1) {
      var stop = at + matched.length;
      var from = -1;
      var to = -1;

      for (var i = 0; i < words.length; i++) {
        if (words[i].end > at && words[i].start < stop) {
          if (from === -1) from = i;
          to = i;
        }
      }

      if (from === -1) return [at, stop];

      for (var q = 0; q < phrases.length; q++) {
        if (!phrases[q].some(function (word) { return sameWord(words[from].word, word); })) continue;

        var span = widen(phrases[q], from, to);

        if (span) return [Math.min(at, words[span[0]].start), Math.max(stop, words[span[1]].end)];
      }

      return [at, stop];
    }

    for (var w = 0; w < words.length; w++) {
      for (var r = 0; r < phrases.length; r++) {
        if (!sameWord(words[w].word, phrases[r][0])) continue;

        var found = widen(phrases[r], w, w);

        if (found) return [words[found[0]].start, words[found[1]].end];
      }
    }

    return null;
  }

  function markText(element, span) {
    var text = element.textContent;
    var mark = document.createElement('mark');

    mark.textContent = text.slice(span[0], span[1]);
    element.textContent = '';
    element.appendChild(document.createTextNode(text.slice(0, span[0])));
    element.appendChild(mark);
    element.appendChild(document.createTextNode(text.slice(span[1])));
  }

  function markMention(mention) {
    if (mention.hasAttribute('data-forum-marked')) return;

    mention.setAttribute('data-forum-marked', '');

    var keywords = [];

    try {
      keywords = JSON.parse(mention.getAttribute('data-forum-keywords') || '[]');
    } catch (error) {
      keywords = [];
    }

    if (!Array.isArray(keywords)) keywords = [];

    var matched = mention.getAttribute('data-forum-matched') || '';
    var quote = mention.querySelector('.forum-mention__quote');
    var title = mention.querySelector('.forum-mention__thread');
    var span = quote ? markSpan(quote.textContent, matched, keywords) : null;

    if (span) {
      markText(quote, span);
      return;
    }

    span = title ? markSpan(title.textContent, matched, keywords) : null;

    if (span) markText(title, span);
  }

  function markMentions(root) {
    var mentions = root.querySelectorAll('.forum-mention[data-post-id]');

    for (var i = 0; i < mentions.length; i++) {
      markMention(mentions[i]);
    }
  }

  function renderMention(template, item, campaign, keywords) {
    var fragment = template.content.cloneNode(true);
    var root = fragment.querySelector('.forum-mention');
    var member = item.member || {};
    var thread = item.thread || {};
    var link = forumLink(item.url, campaign);
    var name = String(member.name || '').trim();

    root.setAttribute('data-post-id', String(item.post_id));
    root.setAttribute('data-forum-keywords', keywords || '[]');
    if (item.matched_text) root.setAttribute('data-forum-matched', item.matched_text);

    var image = root.querySelector('.forum-mention__avatar-image');
    var initial = root.querySelector('.forum-mention__initial');

    if (member.avatar_url) {
      image.src = member.avatar_url;
      removeElement(initial);
    } else {
      removeElement(image);
      setText(initial, name.charAt(0).toUpperCase());
    }

    if (!item.is_best_answer) removeElement(root.querySelector('.forum-mention__best'));

    // "TS" on the avatar of the thread's first post, its note named by the post for the button to point at
    var starter = root.querySelector('.forum-mention__starter');

    if (item.is_thread_start) {
      starter.querySelector('.forum-tip__label').setAttribute('aria-describedby', 'forum-ts-' + item.post_id);
      starter.querySelector('.forum-tip__note').id = 'forum-ts-' + item.post_id;
    } else {
      removeElement(starter);
    }

    setText(root.querySelector('.forum-mention__name'), name);

    // Left empty rather than removed, so the date keeps its place at the end of the line
    setText(root.querySelector('.forum-mention__meta'), member.is_anonymous ? 'Anonym' : [member.title, member.location, member.house].filter(Boolean).join(' · '));

    root.querySelector('.forum-mention__date time').setAttribute('datetime', item.created_at || '');

    setText(root.querySelector('.forum-mention__thread'), thread.title || '');

    // A post that is only a link has no text: "Delade en länk" stands in its place
    var excerpt = String(item.excerpt || '').trim();

    if (excerpt) {
      fillQuote(root.querySelector('.forum-mention__quote'), excerpt, item.matched_text || '');
      removeElement(root.querySelector('.forum-mention__link-only'));
    } else {
      removeElement(root.querySelector('.forum-mention__quote'));
    }

    if (thread.section) {
      setText(root.querySelector('.forum-mention__section'), thread.section);
    } else {
      removeElement(root.querySelector('.forum-mention__section'));
    }

    setText(root.querySelector('.forum-mention__counts'), formatNumber(thread.views) + ' läst · ' + formatNumber(thread.replies) + ' svar');

    // The thread's title, whose link covers the card, opens the post itself, never the thread's start
    root.querySelector('.forum-mention__thread').href = link;

    return fragment;
  }

  function initFeed(feed) {
    if (feed.hasAttribute('data-forum-ready')) return;
    feed.setAttribute('data-forum-ready', '');

    var button = feed.querySelector('[data-forum-more]');
    var list = feed.querySelector('[data-forum-list]');
    // A product page's feeds share one copy, beside them in the card
    var card = feed.closest('.forum-mentions') || feed;
    var template = feed.querySelector('template[data-forum-template]') || card.querySelector('template[data-forum-template]');

    if (!button || !list || !template) return;

    var total = parseInt(feed.getAttribute('data-total'), 10) || 0;
    var nextUrl = feed.getAttribute('data-next-url');
    var shown = {};
    var shownCount = 0;

    var existing = list.querySelectorAll('[data-post-id]');

    for (var i = 0; i < existing.length; i++) {
      shown[existing[i].getAttribute('data-post-id')] = true;
      shownCount++;
    }

    function updateButton() {
      var remaining = total - shownCount;

      if (!nextUrl || remaining <= 0) {
        removeElement(button);
        return;
      }

      button.disabled = false;
      button.textContent = 'Visa fler (' + formatNumber(remaining) + ' kvar)';
    }

    button.addEventListener('click', function () {
      // The button keeps its width through "Hämtar…" and a shorter count, so the pill beside it stays put
      button.style.minWidth = button.getBoundingClientRect().width + 'px';
      button.disabled = true;
      button.textContent = 'Hämtar…';

      fetch(nextUrl, { credentials: 'omit' })
        .then(function (response) {
          if (!response.ok) throw new Error('HTTP ' + response.status);
          return response.json();
        })
        .then(function (data) {
          var items = data.items || [];
          var fragment = document.createDocumentFragment();

          for (var j = 0; j < items.length; j++) {
            var key = String(items[j].post_id);

            if (shown[key]) continue;

            shown[key] = true;
            shownCount++;
            fragment.appendChild(renderMention(template, items[j], feed.getAttribute('data-campaign'), feed.getAttribute('data-forum-keywords')));
          }

          list.appendChild(fragment);
          updateDates(list);
          markMentions(list);

          if (typeof data.total === 'number') total = data.total;
          nextUrl = data.next_url || null;

          // An empty page that still names a next one would loop; stop instead
          if (!items.length) nextUrl = null;

          updateButton();
        })
        .catch(function () {
          button.disabled = false;
          button.textContent = 'Försök igen';
        });
    });
  }

  // "Just nu i forumet" on the homepage: two rows of cards, three cards on phones where they stack, then "Visa fler"
  // and "Upptäck fler omtalade produkter" as under a product page's feed, and each "Visa fler" shows as many more. When
  // the cards run short, the section's next page comes from Shopify's section rendering: fetched ahead as the visitor
  // points at the button and after each click, so a click seldom waits; one that does reads "Hämtar…" until its cards
  // come. Counted again whenever the width or new cards change the rows.
  function stepCards(columns) {
    return columns === 1 ? 3 : columns * 2;
  }

  function initClamp(body) {
    if (body.hasAttribute('data-forum-ready')) return;

    var list = body.querySelector('.forum-latest__list');
    var button = body.querySelector('[data-forum-expand]');
    var section = body.closest('[data-section-id]');

    if (!list || !section) return;

    // From here the script shows the rows, rather than theme.css
    body.setAttribute('data-forum-ready', '');

    var steps = 1;
    var pageParam = body.getAttribute('data-page-param') || 'page';
    var nextPage = parseInt(body.getAttribute('data-next-page'), 10) || null;
    var loading = false;
    var failed = false;
    var seen = {};

    // "Alla inlägg", "Om produkter", "Om produktgrupper", "Om varumärken": the other kinds' cards are hidden. A choice
    // whose cards are still loading is pending, and until it shows, the cards shown before stay. "Visa alla" and
    // "Upptäck fler omtalade produkter", which lead to the page with page numbers, open the same choice there.
    var filter = 'all';
    var pending = null;
    var filterButtons = section.querySelectorAll('[data-forum-filter]');
    var choiceLinks = [section.querySelector('.section__action-link'), body.querySelector('[data-forum-discussed]')]
      .filter(Boolean)
      .map(function (link) {
        return { link: link, href: link.getAttribute('href') || '' };
      });

    function postKey(card) {
      var post = card.querySelector('[data-post-id]');

      return post ? post.getAttribute('data-post-id') : '';
    }

    function matches(card, choice) {
      return choice === 'all' || card.getAttribute('data-forum-type') === choice;
    }

    function countMatching(choice) {
      var count = 0;

      for (var i = 0; i < list.children.length; i++) {
        if (matches(list.children[i], choice)) count++;
      }

      return count;
    }

    function columns() {
      return getComputedStyle(list).gridTemplateColumns.split(' ').length || 1;
    }

    for (var i = 0; i < list.children.length; i++) {
      seen[postKey(list.children[i])] = true;
    }

    // The cards a count of steps shows at the width now: the first ones, then each "Visa fler"'s
    function cardsShown(count) {
      return count * stepCards(columns());
    }

    // The steps' worth of the chosen kind's cards show. "Visa fler" stays while more are loaded or to come.
    function update() {
      if (pending !== null) {
        settle();
        return;
      }

      var limit = cardsShown(steps);
      var count = 0;

      for (var i = 0; i < list.children.length; i++) {
        var card = list.children[i];
        var match = matches(card, filter);

        card.hidden = !match || count >= limit;
        if (match) count++;
      }

      if (count < limit && !failed) fill(limit);

      if (!button) return;

      var waiting = count < limit && loading;

      button.hidden = !(count > limit || nextPage);

      // The button keeps its width through "Hämtar…", so the pill beside it stays put
      if (waiting && !button.disabled) button.style.minWidth = button.getBoundingClientRect().width + 'px';
      if (!waiting) button.style.minWidth = '';

      button.disabled = waiting;
      button.textContent = waiting ? 'Hämtar…' : 'Visa fler';
    }

    // The next page of the section, as on the page now (a theme preview keeps its preview_theme_id), without the
    // cards whose post already shows. The section names its page parameter in data-page-param.
    function load() {
      var url = new URL(window.location.href);

      url.hash = '';
      url.searchParams.set('section_id', section.getAttribute('data-section-id'));
      url.searchParams.set(pageParam, nextPage);

      return fetch(url.toString(), { credentials: 'same-origin' })
        .then(function (response) {
          if (!response.ok) throw new Error('HTTP ' + response.status);
          return response.text();
        })
        .then(function (html) {
          var page = new DOMParser().parseFromString(html, 'text/html');
          var pageBody = page.querySelector('[data-forum-clamp]');
          var cards = page.querySelectorAll('.forum-latest__item');
          var fragment = document.createDocumentFragment();

          for (var j = 0; j < cards.length; j++) {
            var key = postKey(cards[j]);

            if (key && seen[key]) continue;

            var card = document.importNode(cards[j], true);

            // Shown by update() when its row comes
            seen[key] = true;
            card.hidden = true;
            fragment.appendChild(card);
          }

          list.appendChild(fragment);
          updateDates(list);
          markMentions(list);

          // Without the section, or with a page that doesn't move on, stop rather than ask again
          var following = pageBody ? parseInt(pageBody.getAttribute('data-next-page'), 10) || null : null;

          nextPage = following > nextPage ? following : null;
        });
    }

    // Loads pages until the chosen kind has the target's cards, or the list ends. A page that fails stops the loading
    // until the next click.
    function fill(target) {
      if (loading || !nextPage || countMatching(filter) >= target) return;

      loading = true;

      load()
        .then(function () {
          loading = false;
          update();
          fill(target);
        })
        .catch(function () {
          loading = false;
          failed = true;
          update();
        });
    }

    // The cards for the next click
    function prefetch() {
      fill(cardsShown(steps + 1));
    }

    update();

    // The list's size changes with the cards it shows, which settles: the second count shows the same cards
    if (window.ResizeObserver) {
      new ResizeObserver(update).observe(list);
    } else {
      window.addEventListener('resize', update);
    }

    if (button) {
      button.addEventListener('pointerenter', prefetch);
      button.addEventListener('focus', prefetch);
      button.addEventListener('touchstart', prefetch, { passive: true });

      button.addEventListener('click', function () {
        failed = false;
        steps++;
        update();
        prefetch();
      });
    }

    // A pending choice shows once its cards fill the rows, or the list has ended. Until then the cards shown before
    // stay, dimmed as on the page with page numbers, and pages load behind them, so the section changes its height
    // once rather than with each page. A page that fails shows what has come.
    function ready(choice) {
      return countMatching(choice) >= cardsShown(steps);
    }

    function show() {
      filter = pending;
      pending = null;
      section.classList.remove('is-loading');
      section.removeAttribute('aria-busy');
      update();
    }

    function settle() {
      if (pending === null) return;

      if (!nextPage || ready(pending)) {
        show();
        return;
      }

      // A page on its way settles when it comes
      if (loading) return;

      loading = true;

      load()
        .then(function () {
          loading = false;
          settle();
        })
        .catch(function () {
          loading = false;
          show();
        });
    }

    // A choice starts over at the first rows, and its button shows chosen at once
    function choose(choice) {
      pending = choice.getAttribute('data-forum-filter');

      for (var i = 0; i < filterButtons.length; i++) {
        var on = filterButtons[i] === choice;

        filterButtons[i].classList.toggle('button--primary', on);
        filterButtons[i].setAttribute('aria-pressed', on ? 'true' : 'false');
      }

      revealChoice(choice);

      var view = choice.getAttribute('data-forum-view');

      choiceLinks.forEach(function (entry) {
        if (!entry.href || entry.href.indexOf('://') !== -1) return;

        entry.link.setAttribute('href', view ? entry.href + (entry.href.indexOf('?') === -1 ? '?' : '&') + view : entry.href);
      });

      failed = false;
      steps = 1;
      section.classList.add('is-loading');
      section.setAttribute('aria-busy', 'true');
      settle();
    }

    for (var k = 0; k < filterButtons.length; k++) {
      filterButtons[k].addEventListener('click', function () {
        choose(this);
      });
    }
  }

  // A product page's feeds (snippets/forum-mentions.liquid): its own, its brand's and its category's, one at a time.
  // A pill shows its feed's text, count and posts, and names what they are about in the Beta note; each feed keeps
  // its own "Visa fler".
  function initTabs(card) {
    if (card.hasAttribute('data-forum-tabs-ready')) return;
    card.setAttribute('data-forum-tabs-ready', '');

    var pills = card.querySelectorAll('[data-forum-tab]');
    var heads = card.querySelectorAll('[data-forum-pane-head]');
    var feeds = card.querySelectorAll('[data-forum-pane]');
    var subject = card.querySelector('[data-forum-beta-subject]');

    function show(index) {
      for (var i = 0; i < pills.length; i++) {
        var on = i === index;

        pills[i].classList.toggle('button--primary', on);
        pills[i].setAttribute('aria-pressed', on ? 'true' : 'false');
        if (heads[i]) heads[i].hidden = !on;
        if (feeds[i]) feeds[i].hidden = !on;
      }

      if (subject && heads[index]) subject.textContent = heads[index].getAttribute('data-forum-beta-subject');
      revealChoice(pills[index]);
    }

    Array.prototype.forEach.call(pills, function (pill, index) {
      pill.addEventListener('click', function () {
        show(index);
      });
    });
  }

  function refresh(root) {
    updateDates(root);
    markMentions(root);

    var rows = root.querySelectorAll('.forum-latest__filter');

    for (var w = 0; w < rows.length; w++) {
      watchEdges(rows[w]);
    }

    // The page's view, as "Om varumärken", may be out of sight at the row's end on a phone
    var chosen = root.querySelectorAll('.forum-latest__filter .button--primary');

    for (var c = 0; c < chosen.length; c++) {
      revealChoice(chosen[c]);
    }

    var feeds = root.querySelectorAll('[data-forum-feed]');

    for (var i = 0; i < feeds.length; i++) {
      initFeed(feeds[i]);
    }

    var tabbed = root.querySelectorAll('[data-forum-tabs]');

    for (var t = 0; t < tabbed.length; t++) {
      initTabs(tabbed[t]);
    }

    var clamps = root.querySelectorAll('[data-forum-clamp]');

    for (var j = 0; j < clamps.length; j++) {
      initClamp(clamps[j]);
    }
  }

  window.ForumMentions = { refresh: refresh };

  // The badge's link to the feed: theme.js scrolls in-page links to their target less data-offset, so leave the
  // card one card gap under the sticky header, however tall both are at this width, where the sticky info card
  // sits (as the stars' link to the reviews, custom.js). Judge.me draws the reviews above the feed only as the page
  // scrolls past them, which pushes the feed down during the first jump; once the page stops, it scrolls on by
  // whatever the feed moved.
  function headerOffset() {
    var header = document.getElementById('shopify-section-header');
    var cardGap = parseInt(getComputedStyle(document.documentElement).getPropertyValue('--card-gap'), 10) || 0;

    return (header ? header.offsetHeight : 0) + cardGap;
  }

  function settleOn(target) {
    var done = false;

    function correct() {
      if (done) return;
      done = true;
      window.removeEventListener('scrollend', correct);

      var distance = target.getBoundingClientRect().top - headerOffset();

      if (Math.abs(distance) > 3) {
        window.dispatchEvent(new CustomEvent('overflow-scroller:pin'));
        window.scrollBy({ top: distance, behavior: 'smooth' });
      }
    }

    if ('onscrollend' in window) window.addEventListener('scrollend', correct);
    setTimeout(correct, 1500);
  }

  document.addEventListener('click', function (event) {
    var link = event.target.closest ? event.target.closest('a.forum-badge') : null;
    var target = link ? document.querySelector(link.getAttribute('href')) : null;

    if (!target) return;

    link.setAttribute('data-offset', String(headerOffset()));
    settleOn(target);
  }, true);

  // A pill with a note (.forum-tip): "Beta" at the end of a forum section's title (snippets/forum-beta.liquid) and
  // "TS" on a thread starter's avatar (snippets/forum-mention.liquid). The note opens above the pill, or below it when
  // the sticky header or the window's top would cover it, and moves sideways to stay inside the window. Hovering or
  // focus opens it, as does a tap or click; a tap anywhere else or Escape closes it.
  var TIP_MARGIN = 10;

  function headerBottom() {
    var header = document.getElementById('shopify-section-header');

    return header ? Math.max(0, header.getBoundingClientRect().bottom) : 0;
  }

  // A note that wraps keeps the full width its text asked for, with room left at the end of every line, as CSS can't
  // shrink a box to its wrapped lines: it is narrowed to its longest line here
  function fitTipNote(note) {
    note.style.width = '';

    var range = document.createRange();
    var left = Infinity;
    var right = -Infinity;

    range.selectNodeContents(note);

    var lines = range.getClientRects();

    for (var i = 0; i < lines.length; i++) {
      if (!lines[i].width) continue;

      left = Math.min(left, lines[i].left);
      right = Math.max(right, lines[i].right);
    }

    if (right <= left) return;

    var style = getComputedStyle(note);

    note.style.width = Math.ceil(right - left + parseFloat(style.paddingLeft) + parseFloat(style.paddingRight)) + 'px';
  }

  function placeTipNote(tip) {
    var label = tip.querySelector('.forum-tip__label');
    var note = tip.querySelector('.forum-tip__note');

    if (!label || !note) return;

    fitTipNote(note);

    // 14px: the 8px gap and the 6px arrow
    tip.classList.toggle('forum-tip--below', label.getBoundingClientRect().top - headerBottom() < note.offsetHeight + 14);

    // Where the note stands once open, centred over its pill, whichever place it has while closed (theme.css)
    var center = tip.getBoundingClientRect().left + tip.getBoundingClientRect().width / 2;
    var left = center - note.offsetWidth / 2;
    var right = center + note.offsetWidth / 2;
    var width = document.documentElement.clientWidth;
    var shift = 0;

    if (left < TIP_MARGIN) {
      shift = TIP_MARGIN - left;
    } else if (right > width - TIP_MARGIN) {
      shift = width - TIP_MARGIN - right;
    }

    tip.style.setProperty('--forum-tip-shift', shift + 'px');
  }

  function closeTipNotes(except) {
    var open = document.querySelectorAll('.forum-tip.is-open');

    for (var i = 0; i < open.length; i++) {
      if (open[i] !== except) open[i].classList.remove('is-open');
    }
  }

  function closestTip(event) {
    return event.target.closest ? event.target.closest('.forum-tip') : null;
  }

  document.addEventListener('mouseover', function (event) {
    var tip = closestTip(event);

    if (tip && !tip.contains(event.relatedTarget)) placeTipNote(tip);
  });

  document.addEventListener('focusin', function (event) {
    var tip = closestTip(event);

    if (tip) placeTipNote(tip);
  });

  // pointerdown rather than click: Safari on iOS sends no click for a tap on anything but a link or a button
  document.addEventListener('pointerdown', function (event) {
    closeTipNotes(closestTip(event));
  });

  document.addEventListener('click', function (event) {
    var label = event.target.closest ? event.target.closest('[data-forum-tip]') : null;

    if (!label) return;

    placeTipNote(label.parentNode);
    label.parentNode.classList.toggle('is-open');
  });

  document.addEventListener('keydown', function (event) {
    if (event.key === 'Escape') closeTipNotes(null);
  });

  // The page with page numbers (sections/forum-latest.liquid, layout "pages"): its filter and page numbers swap the
  // cards in place rather than load the whole page. The page at the link's address comes from Shopify as it would,
  // its section's content replaces this one's (the section itself stays, with the cart's listeners on it), and the
  // address bar follows, so Back and a shared link still work. A new page scrolls back up to the filter. A link
  // opened in a new tab, or a failed fetch, goes the ordinary way.
  var PAGER_LINKS = '.forum-latest__filter a, .forum-latest .pagination a';

  // Only the latest click's cards land, however the answers come back
  var swapCount = 0;

  function swapSection(url, section, options) {
    var swap = ++swapCount;

    section.classList.add('is-loading');
    section.setAttribute('aria-busy', 'true');

    return fetch(url, { credentials: 'same-origin' })
      .then(function (response) {
        if (!response.ok) throw new Error('HTTP ' + response.status);
        return response.text();
      })
      .then(function (html) {
        if (swap !== swapCount) return;

        var page = new DOMParser().parseFromString(html, 'text/html');
        var fresh = page.querySelector('.forum-latest');

        if (!fresh) throw new Error('No forum section');

        section.innerHTML = fresh.innerHTML;
        document.title = page.title;
        if (options.push) window.history.pushState({ forumLatest: true }, '', url);
        refresh(section);

        var top = section.getBoundingClientRect().top - headerOffset();

        if (options.scroll && top < 0) window.scrollBy({ top: top });
      })
      .catch(function () {
        if (swap === swapCount) window.location.href = url;
      })
      .then(function () {
        if (swap !== swapCount) return;

        section.classList.remove('is-loading');
        section.removeAttribute('aria-busy');
      });
  }

  document.addEventListener('click', function (event) {
    if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

    var link = event.target.closest ? event.target.closest(PAGER_LINKS) : null;

    if (!link || !window.history.pushState) return;

    event.preventDefault();

    // The choice shows at once, before its cards come
    if (link.closest('.forum-latest__filter')) {
      var choices = link.parentNode.querySelectorAll('a');

      for (var i = 0; i < choices.length; i++) {
        choices[i].classList.toggle('button--primary', choices[i] === link);
      }
    }

    swapSection(link.href, link.closest('.forum-latest'), { push: true, scroll: !!link.closest('.pagination') });
  });

  // Back and Forward bring the cards for the address they land on; the page's first entry is marked so its own
  // cards come back too
  if (document.querySelector(PAGER_LINKS) && window.history.replaceState) {
    var firstState = window.history.state || {};

    firstState.forumLatest = true;
    window.history.replaceState(firstState, '');
  }

  window.addEventListener('popstate', function (event) {
    var link = document.querySelector(PAGER_LINKS);

    if (!event.state || !event.state.forumLatest || !link) return;

    swapSection(window.location.href, link.closest('.forum-latest'), { push: false, scroll: false });
  });

  refresh(document);

  document.addEventListener('shopify:section:load', function (event) {
    refresh(event.target);
  });
})();
